/**
 * Employee filters storage service
 * Handles persistence of employee list filters in localStorage
 * Uses @parrot-software/utils for safe storage operations
 * @module domains/personnel/infrastructure/storage/employeeFiltersStorage
 */

import { getStorageItem, setStorageItem, removeStorageItem } from '@parrot-software/utils';
import { PERSONNEL_STORAGE_KEYS, EmployeeFiltersStorage } from './types';

/**
 * Expiration time for stored filters (7 days in milliseconds)
 */
const FILTER_EXPIRATION_DAYS = 7;

/**
 * Saves employee filters to localStorage with timestamp
 *
 * @param {EmployeeFiltersStorage} filters - Employee filters to persist
 * @returns {boolean} Success status - true if saved successfully, false otherwise
 *
 * @example
 * const success = saveEmployeeFilters({
 *   store: ['uuid-1', 'uuid-2'],
 *   role: ['role-uuid'],
 *   status: ['not_verified'],
 *   user: 'john'
 * });
 */
export const saveEmployeeFilters = (filters: EmployeeFiltersStorage): boolean => {
  const dataToStore: EmployeeFiltersStorage = {
    ...filters,
    _timestamp: new Date().toISOString(),
  };

  const result = setStorageItem(PERSONNEL_STORAGE_KEYS.EMPLOYEE_FILTERS, dataToStore);
  return result.success;
};

/**
 * Loads employee filters from localStorage with validation
 *
 * Features:
 * - Returns default filters if storage is empty
 * - Validates data structure and types
 * - Automatically expires filters older than 7 days
 * - Handles corrupted data gracefully
 *
 * @param {EmployeeFiltersStorage} defaultFilters - Default filters to return if storage is empty or invalid
 * @returns {EmployeeFiltersStorage} Loaded filters or defaults
 *
 * @example
 * const filters = loadEmployeeFilters({
 *   store: [],
 *   role: [],
 *   status: [],
 *   user: ''
 * });
 */
export const loadEmployeeFilters = (defaultFilters: EmployeeFiltersStorage): EmployeeFiltersStorage => {
  const result = getStorageItem<EmployeeFiltersStorage>(PERSONNEL_STORAGE_KEYS.EMPLOYEE_FILTERS, defaultFilters);

  // If retrieval failed, return defaults
  if (!result.success || !result.data) {
    return defaultFilters;
  }

  const parsed = result.data;

  // Validate data structure
  if (!parsed || typeof parsed !== 'object') {
    // eslint-disable-next-line no-console
    console.warn('Invalid employee filters data structure in localStorage');
    return defaultFilters;
  }

  // Check expiration (7 days)
  if (parsed._timestamp) {
    const timestamp = new Date(parsed._timestamp);
    const daysSince = (Date.now() - timestamp.getTime()) / (1000 * 60 * 60 * 24);

    if (daysSince > FILTER_EXPIRATION_DAYS) {
      // eslint-disable-next-line no-console
      console.info('Employee filters expired, removing from storage');
      removeEmployeeFilters();
      return defaultFilters;
    }
  }

  // Validate and sanitize each field. Anything stored before a field existed reads as empty:
  // comparing an undefined against an array would break the "are there active filters?" checks.
  return {
    store: Array.isArray(parsed.store) ? parsed.store : [],
    role: Array.isArray(parsed.role) ? parsed.role : [],
    status: Array.isArray(parsed.status) ? parsed.status : [],
    user: typeof parsed.user === 'string' ? parsed.user : '',
  };
};

/**
 * Removes employee filters from localStorage
 *
 * @returns {boolean} Success status - true if removed successfully, false otherwise
 *
 * @example
 * const success = removeEmployeeFilters();
 */
export const removeEmployeeFilters = (): boolean => {
  const result = removeStorageItem(PERSONNEL_STORAGE_KEYS.EMPLOYEE_FILTERS);
  return result.success;
};

/**
 * Checks if employee filters exist in localStorage
 *
 * @returns {boolean} True if filters exist, false otherwise
 *
 * @example
 * if (hasEmployeeFilters()) {
 *   console.log('Filters found in storage');
 * }
 */
export const hasEmployeeFilters = (): boolean => {
  const result = getStorageItem<EmployeeFiltersStorage>(PERSONNEL_STORAGE_KEYS.EMPLOYEE_FILTERS, null as any);
  return result.success && result.data !== null;
};
